(function () {
  const SECTION_SELECTOR = '[data-use-case-reactor]';
  const TAB_TRIGGER_SELECTOR = '[data-use-case-tab-trigger]';
  const PANEL_SELECTOR = '[data-use-case-panel]';
  const CTA_SELECTOR = '[data-use-case-cta]';

  const instances = new Map();

  class UseCaseReactor {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || null;
      this.layout = root.querySelector('[data-use-case-tabs]');
      this.triggers = Array.from(root.querySelectorAll(TAB_TRIGGER_SELECTOR));
      this.panels = Array.from(root.querySelectorAll(PANEL_SELECTOR));
      this.ctas = Array.from(root.querySelectorAll(CTA_SELECTOR));
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
      this.handleCtaClick = this.handleCtaClick.bind(this);

      if (!this.layout || this.triggers.length === 0 || this.panels.length === 0) {
        this.initialized = false;
        return;
      }

      this.initialized = true;
      this.bindEvents();
      this.activateInitialTrigger();
    }

    bindEvents() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
        trigger.addEventListener('keydown', this.handleTriggerKeydown);
      });

      this.ctas.forEach((cta) => {
        cta.addEventListener('click', this.handleCtaClick);
      });
    }

    unbindEvents() {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
        trigger.removeEventListener('keydown', this.handleTriggerKeydown);
      });

      this.ctas.forEach((cta) => {
        cta.removeEventListener('click', this.handleCtaClick);
      });
    }

    activateInitialTrigger() {
      const preActive = this.triggers.find((trigger) => trigger.classList.contains('is-active'));
      const fallback = this.triggers[0];
      this.activateTrigger(preActive || fallback, { emitAnalytics: false });
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      this.activateTrigger(trigger, { focus: false, emitAnalytics: true });
    }

    handleTriggerKeydown(event) {
      const key = event.key;
      const currentTrigger = event.currentTarget;
      const currentIndex = this.triggers.indexOf(currentTrigger);

      if (currentIndex === -1) {
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        event.preventDefault();
        const previousIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
        this.focusTrigger(previousIndex);
      } else if (key === 'ArrowDown' || key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % this.triggers.length;
        this.focusTrigger(nextIndex);
      } else if (key === 'Home') {
        event.preventDefault();
        this.focusTrigger(0);
      } else if (key === 'End') {
        event.preventDefault();
        this.focusTrigger(this.triggers.length - 1);
      }
    }

    focusTrigger(index) {
      const trigger = this.triggers[index];
      if (!trigger) return;
      this.activateTrigger(trigger, { focus: true, emitAnalytics: true });
    }

    activateTrigger(trigger, { focus = false, emitAnalytics = false } = {}) {
      if (!trigger) return;
      const targetId = trigger.dataset.target || trigger.getAttribute('aria-controls');
      if (!targetId) return;

      this.triggers.forEach((element) => {
        const isActive = element === trigger;
        element.classList.toggle('is-active', isActive);
        element.setAttribute('aria-selected', isActive ? 'true' : 'false');
        element.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      let matchedPanel = false;

      this.panels.forEach((panel) => {
        const isActive = panel.id === targetId;
        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          matchedPanel = true;
          panel.removeAttribute('hidden');
          panel.setAttribute('aria-hidden', 'false');
        } else {
          panel.setAttribute('hidden', '');
          panel.setAttribute('aria-hidden', 'true');
        }
      });

      if (!matchedPanel && this.panels.length > 0) {
        const fallbackPanel = this.panels[0];
        fallbackPanel.classList.add('is-active');
        fallbackPanel.removeAttribute('hidden');
        fallbackPanel.setAttribute('aria-hidden', 'false');
      }

      if (focus) {
        trigger.focus();
      }

      if (emitAnalytics) {
        this.trackTabSelect(trigger);
      }
    }

    handleCtaClick(event) {
      const cta = event.currentTarget;
      const tileHandle = cta.dataset.analyticsTile || this.getActiveTileHandle();
      if (window.MadDadAnalytics && typeof window.MadDadAnalytics.track === 'function') {
        window.MadDadAnalytics.track('use_case_tile_click', {
          tile_type: tileHandle,
          destination: cta.getAttribute('href'),
          section_id: this.sectionId,
        });
      }
    }

    trackTabSelect(trigger) {
      const tileHandle = trigger.dataset.analyticsTile || trigger.textContent.trim().toLowerCase().replace(/\s+/g, '_');
      if (window.MadDadAnalytics && typeof window.MadDadAnalytics.track === 'function') {
        window.MadDadAnalytics.track('use_case_tab_select', {
          tile_type: tileHandle,
          section_id: this.sectionId,
        });
      }
    }

    getActiveTileHandle() {
      const activeTrigger = this.triggers.find((trigger) => trigger.classList.contains('is-active'));
      return activeTrigger ? activeTrigger.dataset.analyticsTile : undefined;
    }

    destroy() {
      this.unbindEvents();
    }
  }

  function initSection(root) {
    if (!root || instances.has(root)) return;
    const instance = new UseCaseReactor(root);
    if (instance.initialized) {
      instances.set(root, instance);
    }
  }

  function teardownSection(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', initAll);

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.closest(SECTION_SELECTOR) || event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.closest(SECTION_SELECTOR) || event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      teardownSection(section);
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.closest(SECTION_SELECTOR) || event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const section = event.target.closest(SECTION_SELECTOR) || event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initSection(section);
    }
  });
})();
